import axios from "axios"
import errorHandler from "@/lib/errorHandler"
import msgHandler from "@/lib/msgHandler"
import nprogress from "@/lib/NProgress"
import localforage from "localforage"
import cacheHandler from "@/lib/cacheHandler"
import Auth from "@/services/Auth"
import {store} from "@/store"
import FileSaver from "file-saver"
const server_api_url = (window.__CONFIG__ && window.__CONFIG__.SERVER_API_URL && window.__CONFIG__.SERVER_API_URL !== '${SERVER_API_URL}') ?
  window.__CONFIG__.SERVER_API_URL : import.meta.env.VITE_SERVER_API_URL

const TIMEOUT = 8 * 60 * 1000
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000
const CACHE_LIMIT = 100
const CACHE_INDEX_KEY = "__http_cache_index__"
const CACHE_KEY_PREFIX = "api/"

let requestsCount = 0
let changeLoading = false
let refreshingCall = undefined

let api = axios.create({
  baseURL: server_api_url + "api/",
  timeout: TIMEOUT,
  responseType: "json",
  headers: {
    "Content-Type": "application/json",
  },
})
api.interceptors.request.use(function (config) {
  if (requestsCount === 0) nprogress.start()
  requestsCount++
  config.headers["Authorization"] = Auth.getToken()
  config.headers["organizationId"] = store.getters["Contexts/getOrganization"]
  config.headers["module"] = store.getters["App/getModule"] || Auth.getModules()[0]
  let year = store.getters["Contexts/getYear"]
  let echelonType = store.getters["Contexts/getEchelonType"]
  if (year && echelonType !== undefined) {
    config.headers["year"] = year
    config.headers["echelonType"] = store.getters["Contexts/getEchelonType"]
  }
  return config
}, function (error) {
  requestsCount--
  if (requestsCount === 0) nprogress.done()
  return Promise.reject(error)
})



api.interceptors.response.use(function (response) {
  requestsCount--
  if (requestsCount === 0) nprogress.done()
  msgHandler.handle(response)

  return response
}, async function (error) {
  requestsCount--
  if (requestsCount === 0) nprogress.done()
  if (error.response && error.response.status === 401) {
    console.log(error.response)
    return setRefreshToken(_ => {
      console.log("setRefreshToken")
      error.config.headers['Authorization'] = Auth.getToken()
      return api.request(error.config);
    }).catch(() => {
      console.log("catch")
    })
  }
  else {
    await errorHandler.handle(error)
  }

  return Promise.reject(error)
})

// stealh
let apiStealth = axios.create({
  baseURL: server_api_url + "api/",
  timeout: TIMEOUT,
  responseType: "json",
  headers: {
    "Content-Type": "application/json",
  },
})
apiStealth.interceptors.request.use(function (config) {
  requestsCount++
  config.headers["Authorization"] = Auth.getToken()
  config.headers["organizationId"] = store.getters["Contexts/getOrganization"]
  config.headers["module"] = store.getters["App/getModule"] || Auth.getModules()[0]
  return config
}, function (error) {
  requestsCount--
  return Promise.reject(error)
})
apiStealth.interceptors.response.use(function (response) {
  requestsCount--
  msgHandler.handle(response)
  return response
}, async function (error) {
  requestsCount--
  await errorHandler.handle(error)
  return Promise.reject(error)
})

async function loadCacheIndex() {
  const index = await localforage.getItem(CACHE_INDEX_KEY)
  return Array.isArray(index) ? index : []
}

async function saveCacheIndex(index) {
  await localforage.setItem(CACHE_INDEX_KEY, index)
}

function serializeParams(params = {}) {
  const entries = Object.entries(params)
  if (!entries.length) return ""
  const searchParams = new URLSearchParams()
  entries.sort(([a], [b]) => a.localeCompare(b)).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== undefined && item !== null) searchParams.append(key, item)
      })
    } else if (value !== undefined && value !== null) {
      searchParams.append(key, value)
    }
  })
  return searchParams.toString()
}

function buildCacheKey(path, options = {}) {
  const serialized = serializeParams(options.params)
  const normalized = serialized ? `${path}?${serialized}` : path
  return `${CACHE_KEY_PREFIX}${normalized}`.toLowerCase()
}

async function removeCacheEntry(key) {
  try {
    await localforage.removeItem(key)
    const index = await loadCacheIndex()
    const nextIndex = index.filter(entry => entry.key !== key)
    if (nextIndex.length !== index.length) {
      await saveCacheIndex(nextIndex)
    }
  } catch (error) {
    // ignore storage errors
  }
}

async function pruneCacheIndex(index) {
  const now = Date.now()
  let changed = false
  for (let i = index.length - 1; i >= 0; i--) {
    const {key, timestamp} = index[i]
    if (now - timestamp > CACHE_MAX_AGE) {
      await localforage.removeItem(key)
      index.splice(i, 1)
      changed = true
    }
  }
  if (changed) await saveCacheIndex(index)
  return index
}

async function writeCacheEntry(key, data) {
  const now = Date.now()
  let index = await loadCacheIndex()
  index = index.filter(entry => entry.key !== key)
  index.push({key, timestamp: now})
  index.sort((a, b) => a.timestamp - b.timestamp)
  while (index.length > CACHE_LIMIT) {
    const oldest = index.shift()
    if (oldest) {
      await localforage.removeItem(oldest.key)
    }
  }
  await localforage.setItem(key, {timestamp: now, data})
  await saveCacheIndex(index)
}

async function readCacheEntry(key) {
  const entry = await localforage.getItem(key)
  if (!entry || typeof entry !== "object") return null
  const {timestamp, data} = entry
  if (!timestamp || Date.now() - timestamp > CACHE_MAX_AGE) {
    await removeCacheEntry(key)
    return null
  }
  return data
}

async function getCachedData(path, options = {}) {
  const cacheKey = buildCacheKey(path, options)
  let index = await loadCacheIndex()
  index = await pruneCacheIndex(index)
  const cached = await readCacheEntry(cacheKey)
  if (cached !== null) return cached
  const response = await api.get(path, options)
  await writeCacheEntry(cacheKey, response.data)
  return response.data
}

export default {
  getCache(path, id, options) {
    let url = id !== undefined ? path + "/" + id : path
    return getCachedData(url, options)
  },
  get(path, id, cancel = {}) {
    let url = id !== undefined ? path + "/" + id : path
    return api.get(url, cancel)
      .then(function (response) {
        return response.data
      })
  },
  fetchGet(path, data, cancel = {}) {
    cacheHandler.findAndRemove(path)
    return api.get(path, data, cancel)
      .then(function (response) {
        let pagination = response.headers["x-pagination"]
        if (pagination) pagination = JSON.parse(pagination)
        return {data: response.data, pagination: pagination}
      })
  },
  post(path, data, cancel = {}) {
    cacheHandler.findAndRemove(path)
    return api.post(path, data, cancel)
      .then(function (response) {
        return response.data
      })
  },
  fetchPost(path, data, cancel = {}) {
    cacheHandler.findAndRemove(path)
    return api.post(path, data, cancel)
      .then(function (response) {
        let pagination = response.headers["x-pagination"]
        if (pagination) pagination = JSON.parse(pagination)
        return {data: response.data, pagination: pagination}
      })
  },
  postStealth(path, data) {
    cacheHandler.findAndRemove(path)
    return apiStealth.post(path, data)
      .then(function (response) {
        return response.data
      })
  },
  async postProgress(path, data) {
    cacheHandler.findAndRemove(path)
    let result = await api.post(path, data)
      .then(function (response) {
        return response.data
      })
    requestsCount--
    if (requestsCount === 0) nprogress.done()
    return result
  },
  put(path, data) {
    cacheHandler.findAndRemove(path)
    return api.put(path, data)
      .then(function (response) {
        return response.data
      })
  },
  delete(path, id) {
    let url = id !== undefined ? path + "/" + id : path
    cacheHandler.findAndRemove(path)
    return api.delete(url)
  },
  deleteByBody(path, body) {
    cacheHandler.findAndRemove(path)
    return api.delete(path, {data: body})
  },
  all(requests) {
    return axios.all(requests)
      .then(axios.spread(function (...responses) {
        return responses
      }))
  },
  downloadPDF(path, data, filename, landscape = true, method = "post") {
    return this.download({method, path, data, filename, fileType: "pdf", landscape})
  },
  downloadXLS(path, data, filename) {
    return this.download({path, data, filename, fileType: "xls"})
  },
  downloadFile(path, filename) {
    return this.download({method: "get", path, filename})
  },
  download({method = "post", path, filename = "", data = {}, config = {}, fileType = "", landscape = true}) {
    // let FileSaver = require("file-saver")
    let suffix = fileType === "xls" ? ".xlsx" : fileType === "pdf" ? ".pdf" : fileType
    config.responseType = "blob"
    if (!config.headers) {
      config.headers = this.getHeaders({fileType, landscape})
    }
    let promise = method === "post" ? api.post(path, data, config) : api.get(path, config)
    return promise.then(function (response) {
      let blob = new Blob([response.data], {type: response.data.type})
      FileSaver.saveAs(blob, `${filename}${suffix}`)
    })
  },
  getHeaders({fileType, landscape}) {
    let headers = {}
    if (fileType === "xls") {
      let accept = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      headers = {"Accept": accept}
    } else if (fileType === "pdf") {
      let accept = "application/pdf" + ((landscape) ? ".landscape" : "")
      headers = {"Accept": accept}
    } else {
      let accept = "application/octet-stream"
      headers = {"Accept": accept}
    }
    return headers
  },
  upload(path, data, config = {}) {
    return api.post(path, data, config)
      .then(function (response) {
        return response.data
      })
  },
  // Agrostream2022
  // m.saidakhmadov@agrostream.net
  uploadXLS(path, file) {
    const form = new FormData()
    form.append("file", file)
    let config = {}
    config.headers = {"Content-Type": `multipart/form-data boundary=${form._boundary}`}
    return this.upload(path, form, config)
  },


}

export function setRefreshToken(cb) {
  if (changeLoading) {
    const chained = refreshingCall.then(cb);
    refreshingCall = chained
    return chained;
  }
  else {
    changeLoading = true
    let refreshingFirstCall = actionRefreshToken().then((res) => {
      store.dispatch("Auth/actionSetTokens", res.data)
      changeLoading = false
      refreshingCall = undefined
    }).catch(() => {
      Auth.logout()
    }).then(cb);
    refreshingCall = refreshingFirstCall
    return refreshingCall;
  }
}

export function actionRefreshToken() {
  let url = `${server_api_url}`
  let config = {headers: {"Content-Type": "application/json", "module": "agroplan"}}
  console.log(JSON.parse(localStorage.getItem('userTokens')))
  return axios.post(
    `${url}api/auth/refresh`, {
      refreshToken: JSON.parse(localStorage.getItem('userTokens'))?.refreshToken
    }, config
  )
}
